import {Input, Spin} from 'antd'
import classNames from 'classnames'
import {createValue} from 'components/form/utils'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {BsEnvelope} from 'react-icons/bs'
import {HiCheckCircle, HiXCircle} from 'react-icons/hi'
import {lookup_lookupInfoByValue_Api} from '../../apis'
import useAsyncAction from '../../modules/asyncCache/useAsyncAction'
import {NullAsDefault} from 'views/Shared'
import {LayoutContext} from '../layouts/Default/LayoutContext'

const LOOKUP_DEBOUNCE = 600

// Đưa params query ra ngoài component scope → object stable, không recreate mỗi render
// Đây là fix quan trọng: nếu để bindQueryParams() bên trong component
// → object mới mỗi render → useAsyncAction nhận props mới → re-render
// → lookupRef.current update giữa debounce → debounce bị cancel
const DEFAULT_EMAIL_QUERY = {
    search_type: 'user',
    input_type: 'email',
}

const EmailLookupField = ({
                              name,
                              handleApply,
                              defaultValue,
                              onChange = NullAsDefault,
                              disabled = false,
                              placeholder = 'ex: email',
                              className,
                              params,  // giữ prop nhưng không dùng để tạo query object inline
                          }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const [fieldValue, setFieldValue] = useState(defaultValue ?? '')
    const [validInfo, setValidInfo] = useState(null)
    const [isLooking, setIsLooking] = useState(false)

    const debounceRef = useRef(null)
    const lastLookedUp = useRef(null)

    const {
        response,
        success,
        handleAsyncAction: lookupEmailGetInfo,
    } = useAsyncAction({
        apiInfo: lookup_lookupInfoByValue_Api,
        query: {
            search_type: params?.search_type ?? DEFAULT_EMAIL_QUERY.search_type,
            input_type: params?.input_type ?? DEFAULT_EMAIL_QUERY.input_type,
        },
    })

    const lookupRef = useRef(lookupEmailGetInfo)
    const handleApplyRef = useRef(handleApply)
    const onChangeRef = useRef(onChange)
    useEffect(() => { lookupRef.current = lookupEmailGetInfo }, [lookupEmailGetInfo])
    useEffect(() => { handleApplyRef.current = handleApply }, [handleApply])
    useEffect(() => { onChangeRef.current = onChange }, [onChange])

    useEffect(() => {
        setIsLooking(false)
        if (success) {
            const userInfo = _.get(response, 'data', {})
            setValidInfo(userInfo)
            handleApplyRef.current({...userInfo, fieldValue: lastLookedUp.current})
        } else {
            setValidInfo({})
            handleApplyRef.current({fieldValue: lastLookedUp.current})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success, response])

    useEffect(() => {
        if (defaultValue) {
            lastLookedUp.current = defaultValue
            lookupRef.current({}, {input_value: defaultValue})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])  // chỉ chạy lúc mount, không re-run khi defaultValue thay đổi

    const triggerLookup = useCallback((value) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        if (!value || !value.includes('@')) {
            setValidInfo(null)
            setIsLooking(false)
            return
        }
        setIsLooking(true)
        debounceRef.current = setTimeout(() => {
            lastLookedUp.current = value
            lookupRef.current({}, {input_value: value})
        }, LOOKUP_DEBOUNCE)
    }, [])

    const handleChange = useCallback((e) => {
        const value = e?.target?.value ?? e
        setFieldValue(value)
        setValidInfo(null)
        onChangeRef.current(createValue(name, value))
        triggerLookup(value)
    }, [name, triggerLookup])

    const suffix = useMemo(() => {
        if (isLooking) return <Spin size="small"/>
        if (!fieldValue) return null
        if (validInfo === null) return null
        if (validInfo?.id) return <HiCheckCircle size={18} className="text-green-500" title={t('account found')}/>
        return <HiXCircle size={18} className="text-orange-400" title={t('not found')}/>
    }, [isLooking, fieldValue, validInfo, t])

    const hintText = useMemo(() => {
        if (!fieldValue) {
            return <span className="text-xs text-color-500 italic">{`* ${t('enter email address')}`}</span>
        }
        if (isLooking) {
            return <span className="text-xs text-color-500 italic">{t('searching')}...</span>
        }
        if (validInfo?.name) {
            return (
                <span className="flex items-center gap-1 text-xs">
                    <HiCheckCircle size={13} className="text-green-500 flex-shrink-0"/>
                    <span className="text-color-500 italic">{t('this email address is associated with the account')}:</span>
                    <span className="font-semibold text-primary">{validInfo.name}</span>
                    <span className="ml-1 text-xs text-green-600 font-medium italic">— {t('auto linked')}</span>
                </span>
            )
        }
        if (validInfo !== null && !validInfo?.name) {
            return (
                <span className="flex items-center gap-1 text-xs text-color-500 italic">
                    <HiXCircle size={13} className="text-orange-400 flex-shrink-0"/>
                    {t('not found email description')}
                </span>
            )
        }
        return null
    }, [fieldValue, isLooking, validInfo, t])

    return (
        <div className="flex flex-col gap-1">
            <Input
                name={name}
                type="email"
                suffix={suffix}
                allowClear={true}
                disabled={disabled}
                value={fieldValue}
                onChange={handleChange}
                placeholder={_.isString(placeholder) ? t(placeholder) : undefined}
                className={classNames('w-full', className)}
                addonBefore={
                    isSm ? (
                        <BsEnvelope style={{marginTop: '0.4rem'}} className="text-color-500"/>
                    ) : (
                        <span className="text-sm text-color-500 italic">{t('email')}</span>
                    )
                }
            />
            {!!hintText && (
                <div className="px-1 min-h-4 transition-all">
                    {hintText}
                </div>
            )}
        </div>
    )
}

export default EmailLookupField